import { SelectionModel } from '@angular/cdk/collections'
import { CdkDragDrop } from '@angular/cdk/drag-drop'
import { FlatTreeControl } from '@angular/cdk/tree'
import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree'
import { Store, Select } from '@ngxs/store'
import { TranslateService } from '@ngx-translate/core'
import { cloneDeep, intersection } from 'lodash'
import { Observable, of as observableOf, Subscription, combineLatest } from 'rxjs'
import { filter, take } from 'rxjs/operators'

import {
  UpdateDataConfiguration,
  AppState,
  CreateNewDataConfiguration,
  CreateDataNewForDeleteDataConfiguration,
  ReorderDataConfiguration,
  InstallerExportDataConfiguration,
  CloneDataConfiguration,
  SetExpandedStates,
  SelectConfiguration,
  CreateLanguageDictionaryUndoable,
  DeleteDataConfigurationUndoable,
  CopyConfiguration,
  PasteConfiguration,
  ImportLegacyConfig,
  GetAssetZoneMapBackground,
  UploadAssetMapView,
  AddTemplateLibraryPiece,
  GetTemplateLibraryPieces,
  PasteFromTemplateLibrary,
} from 'src/app/app.state'
import {
  MENU_OPTION_KEY,
  NODE_LEVELS,
  CONFIRM_DIALOG_TYPE,
  INSTALLER_EXPORT_TYPE,
  EXPANDED_STATES_OPTIONS,
  ERROR_MESSAGES,
  NODE_LEVELS_INDEX,
  GRID_VIEW_POSITION_INDEX,
  REMOVE_CURRENT,
  NOTIFICATION_TYPES,
  Constants,
  CONFIG_TYPE_BY_NODE_LEVEL,
} from 'src/app/constants/internationalized-constants-en'
import { ZoneBoxModel } from 'src/app/models'
import {
  TreeFlatNode,
  TreeNode,
  ZoneLayoutModel,
  SelectedConfigurationModel,
  InspectionDetailModel,
  ZoneNameModel,
  TreeDataType,
  EvirLanguageDictionary,
} from './models/tree.model'
import { DialogComponent } from '../dialog/dialog.component'
import { ConditionEditDialogComponent } from '../condition-edit-dialog/condition-edit-dialog.component'
import { ComponentEditDialogComponent } from '../component-edit-dialog/component-edit-dialog.component'
import { InformDialogComponent } from '../inform-dialog/inform-dialog.component'
import { ConfigLevelEditDialogComponent } from '../config-level-edit-dialog/config-level-edit-dialog.component'
import { ConfirmReflectDialogComponent } from '../confirm-reflect-dialog/confirm-reflect-dialog.component'
import { ZoneLevelEditDialogComponent } from '../zone-level-edit-dialog/zone-level-edit-dialog.component'
import { TreeBuildingService } from './services/tree-building/tree-building.service'
import { DataConfigurationHandlingService } from './services/data-configuration-handling/data-configuration-handling.service'
import { UpdateDataConfigurationUndoable } from '../../app.state'
import { DisableUndoConfigurationChange } from 'src/app/modules/ngxs-history-plugin/actions'
import { NotificationSnackbarService } from '../notification-snackbar/services/notification-snackbar.service'
import { AddPieceDialogComponent } from '../add-piece-dialog/add-piece-dialog.component'
import { LanguageDictionaryHandlingService } from './services/language-dictionary-handling/language-dictionary-handling.service'
import { trimAll } from 'src/app/utils/utils'
import { MenuComponent, MenuOption } from '../menu/menu.component'
import { KeyboardTemplateDragService } from './services/keyboard-template-drag/keyboard-template-drag.service'

type KeyboardDragPath = {
  level: number
  configKey?: string | null
  zoneKey?: string | null
  componentKey?: string | null
  conditionKey?: string | null
}

@Component({
  selector: 'app-tree',
  templateUrl: './tree.component.html',
  styleUrls: ['./tree.component.scss'],
  providers: [TreeBuildingService, DataConfigurationHandlingService]
})
export class TreeComponent implements OnDestroy {

  @Select(AppState.getDataConfiguration) dataConfiguration$: Observable<ZoneLayoutModel[]>
  @Select(AppState.getLanguageDictionary) languageDictionary$: Observable<EvirLanguageDictionary>
  @Select(AppState.getInspectionDetails) dataInspectionDetails$: Observable<InspectionDetailModel[]>
  @Select(AppState.getCompanyId) companyId$: Observable<string>
  @Select(AppState.getExpandedStates) expandedStates$: Observable<string[]>
  @Select(AppState.getSelectedConfiguration) selectedConfiguration$: Observable<SelectedConfigurationModel>
  @Select(AppState.getHoveringAssetZoneBox) hoveringAssetZoneBox$: Observable<ZoneBoxModel>
  @Select(AppState.getActiveUndo) isActiveUndo$: Observable<boolean>
  @Select(AppState.getCopyNodeId) copyNodeId$: Observable<string>
  @Select(AppState.getFilterInspectionTypes) filterInspectionTypes$: Observable<string>
  @Select(AppState.getAccessStatus) isAccessGranted$: Observable<boolean>

  @ViewChild('fileInput') fileInput: ElementRef
  treeControl: FlatTreeControl<TreeFlatNode>
  treeFlattener: MatTreeFlattener<TreeNode, TreeFlatNode>
  dataSource: MatTreeFlatDataSource<TreeNode, TreeFlatNode>
  // expansion model tracks expansion state
  expansionModel = new SelectionModel<string>(true)
  dragging = false
  expandTimeout: any
  expandDelay = 1000
  subscription: Subscription
  languageDictionary: EvirLanguageDictionary
  inspectionDetails: InspectionDetailModel[]
  companyId: string
  dataConfiguration: ZoneLayoutModel[]
  isErrorData: boolean = false
  expandedStates: string[]
  selectedConfiguration: SelectedConfigurationModel
  errorMessage = ERROR_MESSAGES.ERROR_ASSET_CONFIGURATION
  hoveringAssetZoneBox: ZoneBoxModel = null
  statusUndo: boolean = false
  copyNodeId: string = null
  xmlList: string[] = []
  filterInspectionTypes = []
  zoneNameList: ZoneNameModel[] = []
  tooltipDisabledStatus: boolean = true
  isAccessGranted: boolean = false
  tooltipDelayTime = 3000

  private _pendingFocusSelector: string | null = null
  private _pendingDeleteFocusVisibleIndex: number | null = null

  private _menuTriggerSelectorForNodeId(nodeId: string): string {
    return `[data-menu-trigger="true"][data-node-id="${nodeId}"]`
  }

  private _computeNextPrevFocusVisibleIndexAfterDelete(node: TreeFlatNode): number | null {
    if (!node?.id) {
      return null
    }

    const visible = this.visibleNodes()
    const startIndex = visible.findIndex(n => n.id === node.id)
    if (startIndex === -1) {
      return null
    }

    // Skip descendants so "next" means the next peer after the whole subtree.
    const prefix = `${node.id}/`
    let endIndex = startIndex
    for (let i = startIndex + 1; i < visible.length; i++) {
      if (visible[i]?.id?.startsWith(prefix)) {
        endIndex = i
        continue
      }
      break
    }

    const hasNextPeer = endIndex + 1 < visible.length
    if (hasNextPeer) {
      // After deletion, the next peer shifts into startIndex.
      return startIndex
    }

    const hasPrev = startIndex - 1 >= 0
    if (hasPrev) {
      // After deletion, the previous peer remains at startIndex-1.
      return startIndex - 1
    }

    return null
  }

  private _restorePendingDeleteVisibleIndexFocus() {
    if (this._pendingDeleteFocusVisibleIndex === null || this._pendingDeleteFocusVisibleIndex === undefined) {
      return
    }

    const index = this._pendingDeleteFocusVisibleIndex
    this._pendingDeleteFocusVisibleIndex = null

    const tryFocus = () => {
      const nodes = document.querySelectorAll('#configuration-tree mat-tree-node') as NodeListOf<HTMLElement>
      const rowEl = nodes && nodes.length > index ? nodes[index] : null
      const trigger = rowEl?.querySelector('[data-menu-trigger="true"]') as HTMLElement | null
      trigger?.focus()
    }

    setTimeout(tryFocus, 0)
    setTimeout(tryFocus, 50)
    setTimeout(tryFocus, 150)
  }

  private _restoreAfterTreeMutationFocus() {
    // Prefer index-based delete focus (IDs can be renumbered after delete).
    if (this._pendingDeleteFocusVisibleIndex !== null && this._pendingDeleteFocusVisibleIndex !== undefined) {
      this._restorePendingDeleteVisibleIndexFocus()
      return
    }

    if (this._pendingFocusSelector) {
      this._restorePendingFocus()
      return
    }
  }

  keyboardDragNodeId: string | null = null
  keyboardDragActive: boolean = false
  keyboardDragTargetNodeId: string | null = null
  keyboardDropFocusedNodeId: string | null = null

  keyboardTemplateDragActive: boolean = false
  keyboardTemplateSourceNode: TreeFlatNode | null = null
  keyboardTemplatePreviewStyle: { [key: string]: string } = { display: 'none' }
  private _keyboardTemplatePreviewMode: 'template' | 'asset' = 'template'

  private _pendingKeyboardDropFocusPath: KeyboardDragPath | null = null
  private _pendingKeyboardDropFocusClearHandle: any = null
  private _pendingKeyboardDropFocusWaitingForDialogClose: boolean = false

  private keyboardDragPath: KeyboardDragPath | null = null

  menuOptions: MenuOption[] = [
    { text: MENU_OPTION_KEY.IMPORT_LEGACY_CONFIG },
    { text: MENU_OPTION_KEY.IMPORT_JSON },
    { text: MENU_OPTION_KEY.EDIT },
    { text: MENU_OPTION_KEY.COPY },
    { text: MENU_OPTION_KEY.PASTE },
    { text: MENU_OPTION_KEY.CLONE_BELOW },
    { text: MENU_OPTION_KEY.EXPORT_ALL_JSON },
    { text: MENU_OPTION_KEY.EXPORT_CONFIG_JSON },
    { text: MENU_OPTION_KEY.EXPORT_ALL_FULL },
    { text: MENU_OPTION_KEY.EXPORT_ALL_INSTALL },
    { text: MENU_OPTION_KEY.EXPORT_CONFIG_FULL },
    { text: MENU_OPTION_KEY.EXPORT_CONFIG_INSTALL },
    // ECT2-1326: Set disable true to temporarily disable this option
    // EVIRPX-5: Need to make code changes to ensure the "Add to Library" feature works correctly when this option is enabled
    { text: MENU_OPTION_KEY.ADD_TO_LIBRARY, disable: true },
    { text: MENU_OPTION_KEY.DELETE }
  ]

  NODE_LEVELS = NODE_LEVELS

  constructor(
    public treeBuildingService: TreeBuildingService,
    public dataConfigurationHandlingService: DataConfigurationHandlingService,
    private dialog: MatDialog,
    public store: Store,
    private snackbarService: NotificationSnackbarService,
    public langDictionaryService: LanguageDictionaryHandlingService,
    public translateService: TranslateService,
    private keyboardTemplateDragService: KeyboardTemplateDragService,
  ) {
    this.subscription = combineLatest(
      this.dataConfiguration$,
      this.languageDictionary$,
      this.dataInspectionDetails$,
      this.companyId$,
      this.expandedStates$,
      this.selectedConfiguration$,
      this.hoveringAssetZoneBox$,
      this.isActiveUndo$,
      this.copyNodeId$,
      this.filterInspectionTypes$,
      this.isAccessGranted$
    ).subscribe(([dataConfiguration, languageDictionary, dataInspectionDetails, id, expandedNodeStates, selectedConfiguration,
      hoveringAssetZoneBox, statusUndo, copyNodeId, filterInspectionTypes, isAccessGranted]) => {
      this.filterInspectionTypes = filterInspectionTypes
      this.hoveringAssetZoneBox = hoveringAssetZoneBox
      this.copyNodeId = copyNodeId
      this.isAccessGranted = isAccessGranted
      if (
        dataConfiguration && !dataConfiguration.length ||
        languageDictionary && !Object.keys(languageDictionary).length ||
        dataInspectionDetails && !dataInspectionDetails.length ||
        id === '') {
        this.isErrorData = true
        return
      } else if (!dataConfiguration || !languageDictionary || !dataInspectionDetails || !id) {
        return
      }

      const isNeedSetDefaultValue = this.companyId !== id

      this.isErrorData = false
      this.languageDictionary = languageDictionary
      this.inspectionDetails = dataInspectionDetails
      this.dataConfiguration = dataConfiguration
      this.companyId = id
      this.expandedStates = expandedNodeStates
      this.statusUndo = statusUndo

      if (this.statusUndo) {
        this.updateExpandedStatesUndo(this.expandedStates)
        this.store.dispatch(new DisableUndoConfigurationChange())
        // Reload the Asset Map View column after clicking Undo button
        if (this.dataSource && selectedConfiguration) {
          this.store.dispatch(new SelectConfiguration(this.dataSource.data[selectedConfiguration.index]))
        }
      }

      this.createResourceAndRebuildTree(dataConfiguration, languageDictionary)

      // If keyboard dragging is active, the tree rebuild regenerates index-based node ids.
      // Resync the grabbed node id from stable keys so arrow-key moving can continue.
      this.syncKeyboardDragAfterRebuild()

      // If a keyboard drop just occurred, restore focus to the dropped row after rebuild.
      this.schedulePendingKeyboardDropFocusRestore()

      if (isNeedSetDefaultValue) {
        const selectedNode = this.treeControl.dataNodes.length ? this.treeControl.dataNodes[0] : null
        if (selectedNode) {
          this.handleSelectNode(selectedNode, true)
        }
      }

      if (selectedConfiguration) {
        this.selectedConfiguration = selectedConfiguration
      }

      this.zoneNameList = this.treeControl.dataNodes.filter(node => node.level === NODE_LEVELS.ZONE_LEVEL)
        .map(zoneNode => {
          const nodeIndexes = zoneNode.id.split('/').map(value => Number(value))
          return {
            parentIndex: nodeIndexes[NODE_LEVELS_INDEX.CONFIG_LEVEL],
            zoneName: zoneNode.name
          }
        })
    })

    this.subscription.add(
      this.keyboardTemplateDragService.state$.subscribe(state => {
        const wasActive = this.keyboardTemplateDragActive
        this.keyboardTemplateDragActive = !!state.active
        this.keyboardTemplateSourceNode = state.sourceNode

        const inDestination = !!(state as any).inDestination

        if (!this.keyboardTemplateDragActive) {
          this._keyboardTemplatePreviewMode = 'template'
          this.clearKeyboardTemplatePreviewPosition()
          if (!this.keyboardDragActive) {
            this.keyboardDragTargetNodeId = null
          }
          return
        }

        if (!wasActive) {
          // Start preview over the Template Library source row.
          this._keyboardTemplatePreviewMode = 'template'
        }
        if (inDestination) {
          // Template row transferred into Asset Configurations.
          this._keyboardTemplatePreviewMode = 'asset'
        }

        // Initialize drop target to selected config row if possible, otherwise first visible config.
        const visible = this.visibleNodes()
        const configVisible = visible.filter(n => this.dataConfigurationHandlingService.getNodeLevel(n.id) === NODE_LEVELS.CONFIG_LEVEL)
        const selectedIndex = this.selectedConfiguration?.index
        const initialTarget = (selectedIndex !== null && selectedIndex !== undefined)
          ? (configVisible.find(n => {
            const parts = n.id.split('/').map(x => Number(x))
            return parts[NODE_LEVELS_INDEX.CONFIG_LEVEL] === selectedIndex
          }) || configVisible[0])
          : configVisible[0]

        if (initialTarget) {
          // Set an initial drop target, but do not steal focus from the Template Library column.
          // The user can press ArrowRight in the Template Library to move focus into this tree.
          this.keyboardDragTargetNodeId = initialTarget.id
        }

        this.scheduleKeyboardTemplatePreviewPositionUpdate()
      })
    )
  }

  private syncKeyboardDragAfterRebuild() {
    if (!this.keyboardDragActive || !this.keyboardDragPath || !this.treeControl) {
      return
    }

    const matched = this.findFlatNodeByDragPath(this.keyboardDragPath)
    if (matched) {
      this.keyboardDragNodeId = matched.id
      if (!this.keyboardDragTargetNodeId) {
        this.keyboardDragTargetNodeId = matched.id
      }
    } else {
      // If we cannot re-identify the node after a rebuild, end the keyboard drag session.
      this.keyboardDragActive = false
      this.keyboardDragNodeId = null
      this.keyboardDragTargetNodeId = null
      this.keyboardDragPath = null
    }
  }

  updateExpandedStatesUndo(expandedStates: string[]) {
    this.expansionModel.clear()
    expandedStates.forEach(expanded => {
      this.expansionModel.toggle(expanded)
    })
  }

  createResourceAndRebuildTree(dataConfiguration: ZoneLayoutModel[], languageDictionary: EvirLanguageDictionary) {
    const treeData = this.treeBuildingService.getConfigurations(dataConfiguration, languageDictionary)
    const data = this.treeBuildingService.buildTree(treeData, 0)
    const transformer = (node: TreeNode, level: number) => {
      return new TreeFlatNode(!!node.children, node.name, level, node.value, node.id, node.nodeType, node.reflectCount)
    }
    const getLevel = (node: TreeFlatNode) => node.level
    const isExpandable = (node: TreeFlatNode) => node.expandable
    const getChildren = (node: TreeNode): Observable<TreeNode[]> => observableOf(node.children)
    this.treeFlattener = new MatTreeFlattener(transformer, getLevel, isExpandable, getChildren)
    this.treeControl = new FlatTreeControl<TreeFlatNode>(getLevel, isExpandable)
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener)

    this.rebuildTreeForData(data)
  }
  hasChild = (_index: number, _nodeData: TreeFlatNode) => {
    const nodeSiblings = this.treeBuildingService.findNodeSiblings(this.dataSource.data, _nodeData.id)
    let node = nodeSiblings.find(x => x.id === _nodeData.id)
    if (!node || !node.children || !node.children.length) {
      _nodeData.expandable = false
      return false
    }
    return _nodeData.expandable
  }

  /**
   * This constructs an array of nodes that matches the DOM
   */
  visibleNodes(): TreeNode[] {
    const result = []

    function addExpandedChildren(node: TreeNode, expanded: string[]) {
      result.push(node)
      if (expanded.includes(node.id) && node.children) {
        node.children.map((child) => addExpandedChildren(child, expanded))
      }
    }
    this.dataSource.data.forEach((node) => {
      addExpandedChildren(node, this.expansionModel.selected)
    })
    return result
  }

  /**
   * Handle the drop - here we rearrange the data based on the drop event,
   * then rebuild the tree.
   * */
  drop(event: CdkDragDrop<string[]>) {
    // console.log('origin/destination', event.previousIndex, event.currentIndex);

    // ignore drops outside of the tree
    if (!event.isPointerOverContainer) {
      return
    }


    // construct a list of visible nodes, this will match the DOM.
    // the cdkDragDrop event.currentIndex jives with visible nodes.
    // it calls rememberExpandedTreeNodes to persist expand state
    const visibleNodes = this.visibleNodes()

    // deep clone the data source so we can mutate it
    const changedData = cloneDeep(this.dataSource.data)
    const isDropBelow = event.currentIndex > event.previousIndex

    let nodeAtDest = visibleNodes[event.currentIndex]
    let newSiblings, node
    if (event && event.item) {
      node = event.item.data
    }


    if (nodeAtDest) {
      newSiblings = this.treeBuildingService.findNodeSiblings(changedData, nodeAtDest.id)
    }

    if (!newSiblings && event.currentIndex === visibleNodes.length) {
      const insertLevel = NODE_LEVELS_INDEX.CONFIG_LEVEL
      const dropLevel = 0
      nodeAtDest = {
        id: `0/${event.currentIndex}`
      } as TreeNode
      this.handleConfirmAfterDropFromTemplateLibrary(insertLevel, node, nodeAtDest, event.currentIndex, dropLevel)
      return
    }

    // determine where to insert the node
    if (!newSiblings) {
      return
    }

    const insertIndex = newSiblings.findIndex(s => s.id === nodeAtDest.id)

    if (event.item.data && event.item.data.nodeType === TreeDataType.TEMPLATE_LIBRARY) {
      const insertParentsIndex: number[] = nodeAtDest.id.split('/').map(x => Number(x))
      const insertLevel = insertParentsIndex.length - 1
      const insertParentsDropNodeIndex: number[] = node.id.split('/').map(x => Number(x))
      const insertDropNodeLevel = this.dataConfigurationHandlingService.getNodeLevel(node.id)
      this.handleConfirmAfterDropFromTemplateLibrary(insertLevel, node, nodeAtDest, event.currentIndex, insertDropNodeLevel)
      return
    }

    const siblings = this.treeBuildingService.findNodeSiblings(changedData, node.id)
    const siblingIndex = siblings.findIndex(n => n.id === node.id)

    const nodeToInsert = siblings[siblingIndex]

    if (nodeAtDest.id === nodeToInsert.id) {
      return
    }

    // ensure validity of drop - must be same level
    const nodeAtDestFlatNode = this.treeControl.dataNodes.find((n) => nodeAtDest.id === n.id)
    const parentNodeAtDestFlatNode = this.getParentNode(nodeAtDestFlatNode)
    const parentCurrentNode = this.getParentNode(node)

    // Handle exception for bug ECT2-61, ECT2-62
    if (nodeAtDestFlatNode.level !== node.level) {
      const newDropIndex = this.handleReorderException(node.id, event.currentIndex, isDropBelow)

      if (newDropIndex !== -1) {
        this.handleReorderNode(node, newDropIndex)
        this.updateExpandedStates(node, EXPANDED_STATES_OPTIONS.DRAG_DROP, parentNodeAtDestFlatNode)
        this.handleDragDropSelectConfiguration(node.level, this.selectedConfiguration.index, this.dataSource.data, siblingIndex, newDropIndex)
      }
      return

    }
    const isNodeAtDestExpanded = this.expansionModel.selected.find(x => x === nodeAtDest.id)
    const nextNode = visibleNodes[event.currentIndex + 1]
    const nodeLevel = this.dataConfigurationHandlingService.getNodeLevel(node.id)
    const isSameParent = nodeAtDest.id.split('/', nodeLevel + 1).join('/') === node.id.split('/', nodeLevel + 1).join('/')
    if (isDropBelow &&
      nextNode &&
      isNodeAtDestExpanded &&
      nodeAtDest.children.length &&
      this.dataConfigurationHandlingService.getNodeLevel(node.id) !== this.dataConfigurationHandlingService.getNodeLevel(nextNode.id) ||
      (!isSameParent && nodeLevel !== NODE_LEVELS.CONFIG_LEVEL)) {
      return
    }
    if (!isSameParent) {
      return
    }

    this.handleReorderNode(node, insertIndex)
    this.updateExpandedStates(node, EXPANDED_STATES_OPTIONS.DRAG_DROP, nodeAtDestFlatNode)
    this.handleDragDropSelectConfiguration(node.level, this.selectedConfiguration.index, this.dataSource.data, siblingIndex, insertIndex)
  }

  handleAfterDrop(node: TreeNode, nodeAtDest: TreeNode, insertAtLast: boolean = false) {
    this.store.dispatch(new PasteFromTemplateLibrary(node, nodeAtDest.id)).subscribe(
      () => {
        this.updateExpandedStatesWhenDropFromTemplateLibrary(nodeAtDest, insertAtLast)
      }
    )
  }

  handleConfirmAfterDropFromTemplateLibrary(insertLevel: NODE_LEVELS_INDEX, node: TreeNode, nodeAtDest: TreeNode, dropIndex: number, insertDropNodeLevel: NODE_LEVELS) {

    let insertNode = cloneDeep(nodeAtDest)
    let insertAtLast = false
    if (insertLevel !== NODE_LEVELS_INDEX.CONFIG_LEVEL) {
      insertNode = this.handleExceptionDropFromTemplateLibrary(dropIndex, insertLevel, insertDropNodeLevel)
      insertAtLast = true
      if (!insertNode) {
        return
      }
    }

    // ECT2-1184: No need to open dialog confirm Include all sublayers
    this.handleAfterDrop(node, insertNode, insertAtLast)
  }

  handleReorderNode(node: TreeNode, newIndex: number) {
    // open confirm dialog when reodering condition node (has reflect data) on the tree
    if (node.reflectCount && node.reflectCount.count > 1 && node.id.split('/').length === 5) {
      const dialogRef = this.dialog.open(ConfirmReflectDialogComponent, {
        data: {
          onClose: () => {
            dialogRef.close()
          },
          onApplyJustThisOne: () => {
            this.store.dispatch(new ReorderDataConfiguration(node, newIndex, false))
            dialogRef.close()
          },
          onApplyAll: () => {
            this.store.dispatch(new ReorderDataConfiguration(node, newIndex, true))
            dialogRef.close()
          },
          cancelText: this.translateService.instant('CANCEL'),
          applyJustThisOneText: this.translateService.instant('APPLY_ONE'),
          applyAllText: this.translateService.instant('APPLY_ALL'),
          title: this.translateService.instant('REFLECT_REORDERING_TITLE'),
          message: this.translateService.instant('REFLECT_REORDERING_CONFIRM_MSG'),
          focusApplyJustThisOne: true,
        },
        maxWidth: '594px'
      })
    } else {
      this.store.dispatch(new ReorderDataConfiguration(node, newIndex, true))
    }
  }

  // Update position select configuration when drag drop
  handleDragDropSelectConfiguration(nodeLevel: number, positionSelectedConfiguration: number, nodes: TreeNode[], positionDrag: number, positionDrop: number) {
    if (nodeLevel !== NODE_LEVELS.CONFIG_LEVEL) {
      return
    }
    let newPosition: number

    if (positionDrag > positionSelectedConfiguration && positionDrop <= positionSelectedConfiguration) {
      newPosition = positionSelectedConfiguration + 1
    } else if (positionDrag < positionSelectedConfiguration && positionDrop >= positionSelectedConfiguration) {
      newPosition = positionSelectedConfiguration - 1
    } else {
      if (positionDrag === positionSelectedConfiguration) {
        newPosition = positionDrop
      } else {
        return
      }
    }

    // const flatNode = new TreeFlatNode(false, nodes[newPosition].name, nodes[newPosition]., node.value, node.id, node.reflectCount)
    // this.store.dispatch(new CopyConfiguration(flatNode))
    this.store.dispatch(new SelectConfiguration(nodes[newPosition]))
  }

  rebuildTreeForData(data: any) {
    this.dataSource.data = data
    this.expansionModel.selected.forEach((id) => {
      const node = this.treeControl.dataNodes.find((n) => n.id === id)
      this.treeControl.expand(node)
    })
  }

  private getParentNode(node: TreeFlatNode): TreeFlatNode | null {
    const currentLevel = node.level
    if (currentLevel < 1) {
      return null
    }
    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1
    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i]
      if (currentNode.level < currentLevel) {
        return currentNode
      }
    }
    return null
  }

  isActiveNode(node: TreeFlatNode) {
    if (!this.selectedConfiguration) {
      return false
    }
    const parentsIndex: number[] = node.id.split('/').map(x => Number(x))
    const configIndex = parentsIndex[NODE_LEVELS_INDEX.CONFIG_LEVEL]
    return configIndex === this.selectedConfiguration.index
  }

  handleSelectOption(option, node: TreeFlatNode) {
    switch (option) {
      case MENU_OPTION_KEY.DELETE:
        if (node.reflectCount && node.reflectCount.count > 1 && node.level === NODE_LEVELS.CONDITION_LEVEL) {
          this.openConfirmRemoveReflectDialog(node)
        } else {
          this.openConfirmDialog(node)
        }
        break
      case MENU_OPTION_KEY.EDIT:
        this.openEditDialog(node)
        break
      case MENU_OPTION_KEY.EXPORT_CONFIG_JSON:
        const nodeSiblings = this.treeBuildingService.findNodeSiblings(this.dataSource.data, node.id)
        const selectedNode = nodeSiblings.find(x => x.id === node.id)
        const result = this.dataConfigurationHandlingService.convertTreeDataToJson(selectedNode, this.languageDictionary)
        this.openInformDialog(JSON.stringify(result, null, 2))
        break
      case MENU_OPTION_KEY.EXPORT_ALL_JSON:
        const rawLanguageDictionary = this.store.selectSnapshot(AppState.getRawLanguageDictionary)
        const languageCodes = this.store.selectSnapshot(AppState.getLanguageCodes)
        const configPackageVersion = this.store.selectSnapshot(AppState.getConfigPackageVersion)
        const createdDate = this.store.selectSnapshot(AppState.getConfigPackageCreatedDate)
        const configType = this.store.selectSnapshot(AppState.getGlobalConfigType)
        const isWorkInProgress = this.store.selectSnapshot(AppState.getWorkInProgressStatus)
        const isDraft = this.store.selectSnapshot(AppState.getDraftStatus)
        const allConfig = this.dataConfigurationHandlingService.convertAllTreeDataToJson(
          this.dataSource.data, this.inspectionDetails, rawLanguageDictionary, this.companyId,
          configType, isWorkInProgress, isDraft, this.languageDictionary, languageCodes, configPackageVersion, createdDate
        )
        this.openInformDialog(JSON.stringify(allConfig, null, 2))
        break
      case MENU_OPTION_KEY.EXPORT_ALL_FULL:
      case MENU_OPTION_KEY.EXPORT_CONFIG_FULL:
        if (node) {
          this.store.dispatch(new InstallerExportDataConfiguration(INSTALLER_EXPORT_TYPE.INSTALLER_EXPORT_LARGE, false, node))
        } else {
          this.store.dispatch(new InstallerExportDataConfiguration(INSTALLER_EXPORT_TYPE.INSTALLER_EXPORT_LARGE, false))
        }
        break
      case MENU_OPTION_KEY.EXPORT_ALL_INSTALL:
      case MENU_OPTION_KEY.EXPORT_CONFIG_INSTALL:
        if (node) {
          this.store.dispatch(new InstallerExportDataConfiguration(INSTALLER_EXPORT_TYPE.INSTALLER_EXPORT_SHORT, false, node))
        } else {
          this.store.dispatch(new InstallerExportDataConfiguration(INSTALLER_EXPORT_TYPE.INSTALLER_EXPORT_SHORT, false))
        }
        break
      case MENU_OPTION_KEY.CLONE_BELOW:
        if (node?.id) {
          this._pendingFocusSelector = `[data-menu-trigger="true"][data-node-id="${node.id}"]`
        }
        this.store.dispatch(new CloneDataConfiguration(node)).subscribe(
          () => {
            this.updateExpandedStates(node, EXPANDED_STATES_OPTIONS.CLONE_BELOW)
            if (node?.id) {
              this._restorePendingFocus()
            } else {
              this._restoreLastMenuTriggerFocus()
            }
          }
        )
        break
      case MENU_OPTION_KEY.COPY:
        if (node?.id) {
          this._pendingFocusSelector = `[data-menu-trigger="true"][data-node-id="${node.id}"]`
        }
        this.store.dispatch(new CopyConfiguration(node))
        if (node?.id) {
          this._restorePendingFocus()
        } else {
          this._restoreLastMenuTriggerFocus()
        }
        break
      case MENU_OPTION_KEY.PASTE:
        if (node?.id) {
          this._pendingFocusSelector = `[data-menu-trigger="true"][data-node-id="${node.id}"]`
        }
        this.store.dispatch(new PasteConfiguration(node)).subscribe(
          () => {
            this.updateExpandedStates(node, EXPANDED_STATES_OPTIONS.COPY_PASTE)
            if (node?.id) {
              this._restorePendingFocus()
            } else {
              this._restoreLastMenuTriggerFocus()
            }
          }
        )
        break
      case MENU_OPTION_KEY.IMPORT_LEGACY_CONFIG:
        this.openFileUploadDialog()
        break
      case MENU_OPTION_KEY.ADD_TO_LIBRARY:
        this.openAddPieceDialog(node)
        break
      default:
        break
    }
  }

  openFileUploadDialog() {
    const element: HTMLElement = this.fileInput.nativeElement
    element.click()
  }

  chooseFile(fileInputEvent: any) {
    const listDataFileChosen = Object.values(fileInputEvent.target.files)

    listDataFileChosen.forEach((item: File) => {
      this.uploadXMLFile(item)
    })
    /**
    * File will have content the latest selected file
    * When user click Cancel in dialog reference won't be changed and still contains the previously selected file.
    *
    */
    if (fileInputEvent.target.files[0]) {
      setTimeout(() => {
        if (this.xmlList && this.xmlList.length) {
          this.handleInputXMLFile(this.xmlList)
        }
      }, 100)
    }
    // clear input file and reset array
    this.fileInput.nativeElement.value = ''
    this.xmlList.length = 0
  }

  handleInputXMLFile(xmlList: string[]) {
    const oldConfigLength = this.dataConfiguration.length
    this.store.dispatch(new ImportLegacyConfig(xmlList)).subscribe(
      () => {
        const numberNewConfig = this.dataConfiguration.length - oldConfigLength
        this.updateExpandedStates(null, EXPANDED_STATES_OPTIONS.IMPORT_LEGACY,
          null, numberNewConfig)
      }
    )
  }

  uploadXMLFile(file: File) {
    let fileReader = new FileReader()
    fileReader.onload = (e) => {
      const contentXML: string = fileReader.result as string
      this.xmlList.push(contentXML)
    }
    fileReader.readAsText(file)
  }

  removeNodeInTree(node: TreeFlatNode, confirmType: CONFIRM_DIALOG_TYPE) {
    const changedData = cloneDeep(this.dataSource.data)
    const nodeSiblings = this.treeBuildingService.findNodeSiblings(changedData, node.id)
    const nodeDelete = nodeSiblings.find(x => x.id === node.id)
    const nodeIndexDelete = nodeSiblings.findIndex(x => x.id === node.id)
    if (confirmType === CONFIRM_DIALOG_TYPE.CONFIRM_DIALOG) {
      this.store.dispatch(new CreateDataNewForDeleteDataConfiguration(nodeDelete)).subscribe(
        () => {
          this.updateExpandedStates(node, EXPANDED_STATES_OPTIONS.DELETE)
          this.updateSetConfigurationDeleteNode(node, nodeIndexDelete)
          this._restoreAfterTreeMutationFocus()
        }
      )
    } else {
      this.store.dispatch(new DeleteDataConfigurationUndoable(nodeDelete)).subscribe(() => {
        this._restoreAfterTreeMutationFocus()
      })
    }
  }

  updateSetConfigurationDeleteNode(node: TreeFlatNode, nodeIndexDelete: number) {
    if (node.level !== NODE_LEVELS.CONFIG_LEVEL) {
      return
    }
    let indexSetConfiguration: number
    if (this.dataSource.data.length === this.selectedConfiguration.index && this.selectedConfiguration.index === nodeIndexDelete) {
      indexSetConfiguration = 0
    } else if (this.selectedConfiguration.index > nodeIndexDelete) {
      indexSetConfiguration = this.selectedConfiguration.index - 1
    } else if (this.dataSource.data.length === 1 && this.selectedConfiguration.index === nodeIndexDelete) {
      return
    } else if (this.selectedConfiguration.index < nodeIndexDelete) {
      return
    } else {
      indexSetConfiguration = 0
    }
    this.store.dispatch(new SelectConfiguration(this.dataSource.data[indexSetConfiguration]))
  }

  openInformDialog(message: string) {
    const dialogRef = this.dialog.open(InformDialogComponent, {
      data: {
        cancelText: this.translateService.instant('OK'),
        acceptText: this.translateService.instant('COPY'),
        toastMessage: this.translateService.instant('COPIED_TO_CLIPBOARD'),
        title: '',
        message: message
      },
    })

    dialogRef.afterClosed().subscribe(() => {
      const tryFocus = () => {
        MenuComponent.lastTriggerElement?.focus()
      }
      setTimeout(tryFocus, 0)
      setTimeout(tryFocus, 50)
    })
  }

  editNodeInTree(node: TreeFlatNode, newValue: any, isReflect: boolean = true, isUndoable: boolean = true) {
    const changedData = cloneDeep(this.dataSource.data)
    const nodeSiblings = this.treeBuildingService.findNodeSiblings(changedData, node.id)
    const currentNode = nodeSiblings.find(x => x.id === node.id)
    newValue.name = trimAll(newValue.name)
    currentNode.name = newValue.name
    const shouldUpdateAssetViewLocation = node.level === NODE_LEVELS.CONFIG_LEVEL && newValue.assetViewId &&
      !newValue.assetViewGrid.every((value, index) => value === (node.value.assetViewGrid || [])[index])
    const shouldRemovedAssetViewLocation = node.level === NODE_LEVELS.CONFIG_LEVEL && newValue.assetViewId === REMOVE_CURRENT
    const isUpdatedAssetViewBackground = this.checkUpdatedAssetViewBackground(node, newValue.assetViewId)
    this.handleUpdateDataTree(node.level, currentNode, newValue)
    const configType = CONFIG_TYPE_BY_NODE_LEVEL[node.level]
    const newLangKey = this.langDictionaryService.getLangKeyByString(newValue.name, this.languageDictionary)
    if (newLangKey) {
      currentNode.value.id = newLangKey
    }

    if (isReflect) {
      const updateAction = isUndoable ? new UpdateDataConfigurationUndoable(currentNode) : new UpdateDataConfiguration(currentNode)
      const dispatchAction = newLangKey ? updateAction : new CreateLanguageDictionaryUndoable(currentNode, newValue.name, configType)
      this.store.dispatch(dispatchAction).subscribe(
        () => {
          this._handlePostTreeUpdate(currentNode, shouldUpdateAssetViewLocation, shouldRemovedAssetViewLocation)
          if (isUpdatedAssetViewBackground) {
            this.store.dispatch(new GetAssetZoneMapBackground(this.companyId, currentNode.value.assetViewId))
          }
        }
      )
    } else {
      this.store.dispatch(new CreateNewDataConfiguration(currentNode, isReflect, newValue.name, configType))
    }
  }

  handleUpdateDataTree(level: number, currentValue: TreeNode, newValue) {
    switch (level) {
      case NODE_LEVELS.CONFIG_LEVEL:
        currentValue.value.assetType = newValue.assetType.map(value => trimAll(value))
        currentValue.value.assetViewGrid = newValue.assetViewId !== REMOVE_CURRENT
          ? newValue.assetViewGrid
          : [1, 1]
        currentValue.value.assetViewId = newValue.assetViewId !== REMOVE_CURRENT
          ? newValue.assetViewId
          : ''
        return
      case NODE_LEVELS.ZONE_LEVEL:
        currentValue.value.tagNumber = parseInt(newValue.tagNumber, 10)
        currentValue.value.tagType = newValue.tagType
        currentValue.value.zoneInspectionTypes = newValue.zoneInspectionTypes
        currentValue.value.assetViewLocation = newValue.assetViewLocation
        return
      case NODE_LEVELS.COMPONENT_LEVEL:
        currentValue.value.severityMax = newValue.severityMax
        currentValue.value.severityMin = newValue.severityMin
        return
      case NODE_LEVELS.CONDITION_LEVEL:
        return
      default:
        return
    }
  }

  openConfirmDialog(node: TreeFlatNode) {
    const restoreSameEllipsis = () => {
      if (!node?.id) {
        this._restoreLastMenuTriggerFocus()
        return
      }
      this._pendingFocusSelector = this._menuTriggerSelectorForNodeId(node.id)
      this._restorePendingFocus()
    }

    const setPostDeleteEllipsisFocus = () => {
      this._pendingDeleteFocusVisibleIndex = this._computeNextPrevFocusVisibleIndexAfterDelete(node)
      this._pendingFocusSelector = null
    }

    let didConfirmDelete = false

    const dialogRef = this.dialog.open(DialogComponent, {
      data: {
        onClose: () => { dialogRef.close() },
        onAccept: () => {
          didConfirmDelete = true
          setPostDeleteEllipsisFocus()
          this.removeNodeInTree(node, CONFIRM_DIALOG_TYPE.CONFIRM_DIALOG)
          dialogRef.close()
        },
        onCancel: () => {
          restoreSameEllipsis()
        },
        cancelText: this.translateService.instant('NO'),
        acceptText: this.translateService.instant('YES'),
        title: this.translateService.instant('DELETE_TITLE'),
        message: this.translateService.instant('CONFIG_DELETE_CONFIRM_MSG'),
        focusAccept: true,
      },
    })

    dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
      if (!didConfirmDelete) {
        restoreSameEllipsis()
      }
    })
  }

  openConfirmRemoveReflectDialog(node: TreeFlatNode) {
    const restoreSameEllipsis = () => {
      if (!node?.id) {
        this._restoreLastMenuTriggerFocus()
        return
      }
      this._pendingFocusSelector = this._menuTriggerSelectorForNodeId(node.id)
      this._restorePendingFocus()
    }

    const setPostDeleteEllipsisFocus = () => {
      this._pendingDeleteFocusVisibleIndex = this._computeNextPrevFocusVisibleIndexAfterDelete(node)
      this._pendingFocusSelector = null
    }

    let didConfirmDelete = false

    const dialogRef = this.dialog.open(ConfirmReflectDialogComponent, {
      data: {
        onClose: () => {
          dialogRef.close()
        },
        onApplyJustThisOne: () => {
          didConfirmDelete = true
          setPostDeleteEllipsisFocus()
          this.removeNodeInTree(node, CONFIRM_DIALOG_TYPE.CONFIRM_DIALOG)
          dialogRef.close()
        },
        onApplyAll: () => {
          didConfirmDelete = true
          setPostDeleteEllipsisFocus()
          this.removeNodeInTree(node, CONFIRM_DIALOG_TYPE.REFECLT_DIALOG)
          dialogRef.close()
        },
        cancelText: this.translateService.instant('CANCEL'),
        applyJustThisOneText: this.translateService.instant('APPLY_ONE'),
        applyAllText: this.translateService.instant('APPLY_ALL'),
        title: this.translateService.instant('DELETE_TITLE'),
        message: this.translateService.instant('CONFIG_DELETE_REFERENCED_CONFIRM_MSG'),
        focusApplyJustThisOne: true,
      },
      maxWidth: '594px'
    })

    dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
      if (!didConfirmDelete) {
        restoreSameEllipsis()
      }
    })
  }

  getEditDialogByLevel(level: number) {
    switch (level) {
      case NODE_LEVELS.CONFIG_LEVEL:
        return ConfigLevelEditDialogComponent
      case NODE_LEVELS.ZONE_LEVEL:
        return ZoneLevelEditDialogComponent
      case NODE_LEVELS.COMPONENT_LEVEL:
        return ComponentEditDialogComponent
      case NODE_LEVELS.CONDITION_LEVEL:
        return ConditionEditDialogComponent
      default:
        return
    }
  }

  openEditDialog(node: TreeFlatNode) {

    if (node?.id) {
      this._pendingFocusSelector = `[data-menu-trigger="true"][data-node-id="${node.id}"]`
    }

    const editDialog = this.getEditDialogByLevel(node.level)
    if (!editDialog) {
      return
    }

    let dialogData: any = {
      node: node
    }
    if (node.level === NODE_LEVELS.ZONE_LEVEL) {
      const parentNode = this.getParentNode(node)
      dialogData = {
        ...dialogData,
        assetViewGrid: parentNode.value.assetViewGrid,
        assetViewId: parentNode.value.assetViewId,
      }
    }
    const dialogRef = this.dialog.open(editDialog, {
      data: dialogData,
    })

    const restoreFocus = () => {
      if (node?.id) {
        this._restorePendingFocus()
      } else {
        this._restoreLastMenuTriggerFocus()
      }
    }

    const subscription = dialogRef.afterClosed().pipe(take(1)).subscribe(result => {
      if (result) {
        const { data, isReflect, fileUploaded } = result
        if (data && (!fileUploaded || data.assetViewId !== 'default-selected-id')) {
          this.editNodeInTree(node, data, isReflect)
        } else if (data && fileUploaded && data.assetViewId === 'default-selected-id') {
          fileUploaded.companyId = this.companyId
          this.store.dispatch(new UploadAssetMapView(fileUploaded)).subscribe(
            res => {
              const { app } = res
              const { updateAssetViewMapResponse } = app
              data.assetViewId = res && app && updateAssetViewMapResponse ? updateAssetViewMapResponse.assetViewId : ''
            },
            rawError => {
              const detailedError = typeof (rawError.error) === 'string' ? { message: [rawError.error] } : null
              this.snackbarService.openNotificationSnackbar(
                this.translateService.instant('UPLOAD_ASSET_VIEW_MAP_FAILED'),
                NOTIFICATION_TYPES.FAILED,
                detailedError,
              )
            },
            () => {
              this.editNodeInTree(node, data, isReflect)
            })
        }
      }

      restoreFocus()
    })
  }

  getMenuOption(level: NODE_LEVELS, node?: TreeFlatNode) {
    const copyNodeLevel = this.copyNodeId ? this.dataConfigurationHandlingService.getNodeLevel(this.copyNodeId) : null
    // const isCopyable = copyNodeLevel === null || (copyNodeLevel !== null && this.copyNodeId !== nodeId)
    const isPasteable = copyNodeLevel !== null && (level === copyNodeLevel - 1)

    // Handle to display an options in option menu by level
    switch (level) {
      case NODE_LEVELS.CONFIG_LEVEL:
        return this.menuOptions.filter(menuOption =>
          menuOption.text !== MENU_OPTION_KEY.EXPORT_ALL_JSON &&
          menuOption.text !== MENU_OPTION_KEY.EXPORT_ALL_FULL &&
          menuOption.text !== MENU_OPTION_KEY.EXPORT_ALL_INSTALL &&
          menuOption.text !== MENU_OPTION_KEY.IMPORT_LEGACY_CONFIG &&
          menuOption.text !== MENU_OPTION_KEY.IMPORT_JSON &&
          menuOption.text !== MENU_OPTION_KEY.PASTE &&
          menuOption.text !== MENU_OPTION_KEY.ADD_TO_LIBRARY ||
          menuOption.text === MENU_OPTION_KEY.PASTE && isPasteable ||
          menuOption.text === MENU_OPTION_KEY.ADD_TO_LIBRARY && this.isAccessGranted
        )

      case NODE_LEVELS.ZONE_LEVEL:
      case NODE_LEVELS.COMPONENT_LEVEL:
      case NODE_LEVELS.CONDITION_LEVEL:
        const result = this.menuOptions.filter(menuOption =>
          menuOption.text !== MENU_OPTION_KEY.EXPORT_ALL_JSON &&
          menuOption.text !== MENU_OPTION_KEY.EXPORT_CONFIG_JSON &&
          menuOption.text !== MENU_OPTION_KEY.EXPORT_ALL_FULL &&
          menuOption.text !== MENU_OPTION_KEY.EXPORT_ALL_INSTALL &&
          menuOption.text !== MENU_OPTION_KEY.EXPORT_CONFIG_FULL &&
          menuOption.text !== MENU_OPTION_KEY.EXPORT_CONFIG_INSTALL &&
          menuOption.text !== MENU_OPTION_KEY.IMPORT_LEGACY_CONFIG &&
          menuOption.text !== MENU_OPTION_KEY.IMPORT_JSON &&
          menuOption.text !== MENU_OPTION_KEY.ADD_TO_LIBRARY &&
          menuOption.text !== MENU_OPTION_KEY.PASTE ||
          menuOption.text === MENU_OPTION_KEY.PASTE && isPasteable
        )

        /** 
         * ECT2-1201: Disable Delete option on last condition in a component.
         * Just a short-term solution. This will be removed in the future.
         */
        if (this.dataConfigurationHandlingService.isLastConditionNode(this.dataSource.data, node)) {
          const deleteOptionIndex = result.findIndex(menuOption => menuOption.text === MENU_OPTION_KEY.DELETE)
          if (deleteOptionIndex > -1) {
            const clonedDeleteOption = cloneDeep(result[deleteOptionIndex])
            clonedDeleteOption.disable = true
            clonedDeleteOption.tooltipMessage = this.translateService.instant('AT_LEAST_ONE_CONDITION_TOOLTIP')
            result[deleteOptionIndex] = clonedDeleteOption
          }
        }

        return result
      default:
        return this.menuOptions.filter(menuOption =>
          menuOption.text === MENU_OPTION_KEY.EXPORT_ALL_JSON ||
          menuOption.text === MENU_OPTION_KEY.EXPORT_ALL_FULL ||
          menuOption.text === MENU_OPTION_KEY.EXPORT_ALL_INSTALL ||
          menuOption.text === MENU_OPTION_KEY.IMPORT_LEGACY_CONFIG && this.isAccessGranted ||
          menuOption.text === MENU_OPTION_KEY.IMPORT_JSON ||
          menuOption.text === MENU_OPTION_KEY.PASTE && isPasteable
        )
    }
  }

  setExpandedStatesToStore() {
    const expandedStates = this.expansionModel.selected
    this.store.dispatch(new SetExpandedStates(expandedStates))
  }

  onToggleNode(node: TreeFlatNode) {
    if (!node) {
      return
    }

    // Keep focus stable across store updates + tree rebuilds.
    this._pendingFocusSelector = `[data-focus-type="toggle"][data-node-id="${node.id}"]`

    this.expansionModel.toggle(node.id)
    this.setExpandedStatesToStore()

    this._restorePendingFocus()
  }

  onNodeKeydown(event: KeyboardEvent, node: TreeFlatNode) {
    if (!node) {
      return
    }

    // Clear the post-drop focus marker once the user continues interacting.
    if (this.keyboardDropFocusedNodeId) {
      this.keyboardDropFocusedNodeId = null
    }

    // Stop any pending post-drop focus forcing once the user interacts again.
    if (this._pendingKeyboardDropFocusPath) {
      this._pendingKeyboardDropFocusPath = null
    }
    if (this._pendingKeyboardDropFocusClearHandle) {
      clearTimeout(this._pendingKeyboardDropFocusClearHandle)
      this._pendingKeyboardDropFocusClearHandle = null
    }

    // Only handle keyboard DnD when focus is on the row itself.
    // (If focus is on the chevron/menu, those controls should keep their own key handling.)
    if (event.target !== event.currentTarget) {
      return
    }

    const key = this.normalizeKeyboardKey(event.key)

    // Tab while a template drag is in progress cancels the drag and restores focus to the source.
    if (key === 'Tab') {
      if (this.keyboardTemplateDragActive) {
        const sourceNodeId = this.keyboardTemplateSourceNode?.id
        this.keyboardTemplateDragService.cancel()
        this.keyboardTemplateDragActive = false
        this.keyboardTemplateSourceNode = null
        this.keyboardDragTargetNodeId = null
        if (sourceNodeId) {
          event.preventDefault()
          event.stopPropagation()
          this.scheduleTemplateLibraryPostDropFocusRestore(sourceNodeId)
        }
      }
      return
    }

    if (key === 'Escape') {
      if (this.keyboardDragActive || this.keyboardTemplateDragActive) {
        event.preventDefault()
        event.stopPropagation()
        if (this.keyboardTemplateDragActive) {
          const sourceNodeId = this.keyboardTemplateSourceNode?.id
          this.keyboardTemplateDragService.cancel()
          this.keyboardTemplateDragActive = false
          this.keyboardTemplateSourceNode = null
          this.keyboardDragTargetNodeId = null
          if (sourceNodeId) {
            this.scheduleTemplateLibraryPostDropFocusRestore(sourceNodeId)
          }
        } else {
          this.endKeyboardDrag()
        }
      }
      return
    }

    if (key === 'Enter' || key === ' ') {
      event.preventDefault()
      event.stopPropagation()

      if (this.keyboardTemplateDragActive) {
        // Drop template piece onto the currently focused config row.
        this.keyboardDragTargetNodeId = node.id
        this.commitKeyboardTemplateDrop(node)
        return
      }

      if (!this.keyboardDragActive) {
        // Grab
        this.startKeyboardDrag(node)
      } else {
        // Drop on the currently focused row
        this.keyboardDragTargetNodeId = node.id
        this.commitKeyboardDrop()
      }
      return
    }

    if (!this.keyboardDragActive) {
      if (!this.keyboardTemplateDragActive) {
        return
      }
    }

    // IMPORTANT: stop propagation so MatTree's internal key manager does not move focus
    // (which can make it look like the item was "dropped" after a single arrow press).
    if (key === 'ArrowUp') {
      event.preventDefault()
      event.stopPropagation()
      if (this.keyboardTemplateDragActive) {
        this.keyboardMoveTemplateDropTarget(-1)
      } else {
        this.keyboardMoveDropTarget(-1)
      }
      return
    }

    if (key === 'ArrowDown') {
      event.preventDefault()
      event.stopPropagation()
      if (this.keyboardTemplateDragActive) {
        this.keyboardMoveTemplateDropTarget(1)
      } else {
        this.keyboardMoveDropTarget(1)
      }
      return
    }

    if (key === 'ArrowLeft') {
      event.preventDefault()
      event.stopPropagation()
      if (this.keyboardTemplateDragActive) {
        this.keyboardMoveTemplateDropTarget(-1)
      } else {
        this.keyboardMoveDropTarget(-1)
      }
      return
    }

    if (key === 'ArrowRight') {
      event.preventDefault()
      event.stopPropagation()
      if (this.keyboardTemplateDragActive) {
        this.keyboardMoveTemplateDropTarget(1)
      } else {
        this.keyboardMoveDropTarget(1)
      }
      return
    }
  }

  private keyboardMoveTemplateDropTarget(direction: -1 | 1) {
    if (!this.keyboardTemplateDragActive) {
      return
    }

    const visible = this.visibleNodes()
    const configVisible = visible.filter(n => this.dataConfigurationHandlingService.getNodeLevel(n.id) === NODE_LEVELS.CONFIG_LEVEL)
    if (!configVisible.length) {
      return
    }

    const startId = this.keyboardDragTargetNodeId || configVisible[0].id
    const startIndex = configVisible.findIndex(n => n.id === startId)
    if (startIndex === -1) {
      this.keyboardDragTargetNodeId = configVisible[0].id
      this.focusTreeRowById(configVisible[0].id)
      return
    }

    const nextIndex = startIndex + direction
    if (nextIndex < 0 || nextIndex >= configVisible.length) {
      return
    }

    const next = configVisible[nextIndex]
    this.keyboardDragTargetNodeId = next.id
    this.focusTreeRowById(next.id)

    this._keyboardTemplatePreviewMode = 'asset'
    this.scheduleKeyboardTemplatePreviewPositionUpdate()
  }

  private commitKeyboardTemplateDrop(targetFlatNode: TreeFlatNode) {
    if (!this.keyboardTemplateDragActive || !this.keyboardTemplateSourceNode || !targetFlatNode?.id) {
      return
    }

    const templateSourceId = this.keyboardTemplateSourceNode.id

    // Only allow dropping onto config rows.
    if (targetFlatNode.level !== NODE_LEVELS.CONFIG_LEVEL) {
      return
    }

    // Reuse existing template-library drop flow.
    this.handleAfterDrop(this.keyboardTemplateSourceNode as any, targetFlatNode as any, false)

    // End keyboard template drag.
    this.keyboardTemplateDragService.cancel()
    this.keyboardTemplateDragActive = false
    this.keyboardTemplateSourceNode = null

    // Return focus to the dragged row in Template Library.
    this.scheduleTemplateLibraryPostDropFocusRestore(templateSourceId)
  }

  private scheduleTemplateLibraryPostDropFocusRestore(nodeId: string) {
    if (!nodeId) {
      return
    }

    // Never steal focus from an open dialog.
    if (this.dialog?.openDialogs?.length) {
      this.dialog.afterAllClosed.pipe(take(1)).subscribe(() => {
        this.scheduleTemplateLibraryPostDropFocusRestore(nodeId)
      })
      return
    }

    const selector = `#template-library-tree mat-tree-node[data-node-id="${nodeId}"]`
    const tryFocus = (delayMs: number) => {
      setTimeout(() => {
        // If a dialog appeared after the drop, wait again.
        if (this.dialog?.openDialogs?.length) {
          this.dialog.afterAllClosed.pipe(take(1)).subscribe(() => {
            this.scheduleTemplateLibraryPostDropFocusRestore(nodeId)
          })
          return
        }

        const el = document.querySelector(selector) as HTMLElement | null
        if (!el) {
          return
        }
        if (typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ block: 'nearest' })
        }
        el.focus()
      }, delayMs)
    }

    // Retry a few times to allow: state update -> Angular render -> visibility restore.
    tryFocus(0)
    tryFocus(50)
    tryFocus(150)
    tryFocus(300)
  }

  private normalizeKeyboardKey(key: string): string {
    // Some browsers/embedded contexts can still emit legacy values.
    switch (key) {
      case 'Up':
        return 'ArrowUp'
      case 'Down':
        return 'ArrowDown'
      case 'Left':
        return 'ArrowLeft'
      case 'Right':
        return 'ArrowRight'
      case 'Spacebar':
        return ' '
      default:
        return key
    }
  }

  private startKeyboardDrag(node: TreeFlatNode) {
    this.keyboardDragActive = true
    this.keyboardDragNodeId = node.id
    this.keyboardDragTargetNodeId = node.id
    this.keyboardDragPath = this.buildKeyboardDragPath(node)

    this.keyboardDropFocusedNodeId = null
    this._pendingKeyboardDropFocusPath = null
    if (this._pendingKeyboardDropFocusClearHandle) {
      clearTimeout(this._pendingKeyboardDropFocusClearHandle)
      this._pendingKeyboardDropFocusClearHandle = null
    }

    this.clearKeyboardDragPreviewTransform()
    this.updateKeyboardDragPreviewTransform()
  }

  private endKeyboardDrag() {
    this.clearKeyboardDragPreviewTransform()
    this.keyboardDragActive = false
    this.keyboardDragNodeId = null
    this.keyboardDragTargetNodeId = null
    this.keyboardDragPath = null
  }

  private schedulePendingKeyboardDropFocusRestore() {
    if (!this._pendingKeyboardDropFocusPath) {
      return
    }

    // Never steal focus from an open dialog (e.g. reflect reorder confirmation).
    // Instead, retry once dialogs are closed.
    if (this.dialog?.openDialogs?.length) {
      if (!this._pendingKeyboardDropFocusWaitingForDialogClose) {
        this._pendingKeyboardDropFocusWaitingForDialogClose = true
        this.dialog.afterAllClosed.pipe(take(1)).subscribe(() => {
          this._pendingKeyboardDropFocusWaitingForDialogClose = false
          this.schedulePendingKeyboardDropFocusRestore()
        })
      }
      return
    }

    const tryRestore = (delayMs: number) => {
      setTimeout(() => {
        if (!this._pendingKeyboardDropFocusPath || !this.treeControl) {
          return
        }

        const matched = this.findFlatNodeByDragPath(this._pendingKeyboardDropFocusPath)
        if (matched) {
          const selector = `#configuration-tree mat-tree-node[data-node-id="${matched.id}"]`
          const el = document.querySelector(selector) as HTMLElement | null
          if (el) {
            el.focus()

            // Ensure no other pending focus restore overrides the dropped-row focus.
            this._pendingFocusSelector = null
            this._pendingDeleteFocusVisibleIndex = null

            // Mark the dropped row so it has a visible focus ring even if :focus-visible doesn't apply.
            this.keyboardDropFocusedNodeId = matched.id

            // Keep pending focus alive briefly: reorder can trigger multiple rapid tree rebuilds
            // (e.g. selected configuration updates) that can steal focus.
            if (!this._pendingKeyboardDropFocusClearHandle) {
              this._pendingKeyboardDropFocusClearHandle = setTimeout(() => {
                this._pendingKeyboardDropFocusPath = null
                this._pendingKeyboardDropFocusClearHandle = null
              }, 500)
            }
          }
        }
      }, delayMs)
    }

    // Retry a few times to allow:
    // - store update -> rebuild -> flattening
    // - Angular render
    // - any scroll/focus side-effects
    tryRestore(0)
    tryRestore(50)
    tryRestore(150)
    tryRestore(300)
  }

  private keyboardMoveDropTarget(direction: -1 | 1) {
    if (!this.keyboardDragActive) {
      return
    }

    const dragged = this.getKeyboardDraggedFlatNode()
    if (!dragged) {
      return
    }

    const targetId = this.keyboardDragTargetNodeId || dragged.id
    const visible = this.visibleNodes()
    const nodeLevel = this.dataConfigurationHandlingService.getNodeLevel(dragged.id)
    const parentKey = dragged.id.split('/', nodeLevel + 1).join('/')

    const startIndex = visible.findIndex(n => n.id === targetId)
    if (startIndex === -1) {
      return
    }

    for (let i = startIndex + direction; i >= 0 && i < visible.length; i += direction) {
      const candidate = visible[i]
      const candidateLevel = this.dataConfigurationHandlingService.getNodeLevel(candidate.id)
      const candidateParentKey = candidate.id.split('/', nodeLevel + 1).join('/')
      if (candidateLevel === nodeLevel && candidateParentKey === parentKey) {
        this.keyboardDragTargetNodeId = candidate.id
        this.focusTreeRowById(candidate.id)
        this.scheduleKeyboardDragPreviewTransformUpdate()
        return
      }
    }
  }

  private scheduleKeyboardDragPreviewTransformUpdate() {
    setTimeout(() => this.updateKeyboardDragPreviewTransform(), 0)
    setTimeout(() => this.updateKeyboardDragPreviewTransform(), 50)
  }

  private updateKeyboardDragPreviewTransform() {
    if (!this.keyboardDragActive) {
      return
    }

    const dragged = this.getKeyboardDraggedFlatNode()
    const draggedId = dragged?.id || this.keyboardDragNodeId
    const targetId = this.keyboardDragTargetNodeId
    if (!draggedId || !targetId) {
      return
    }

    const grabbedEl = document.querySelector(
      `#configuration-tree mat-tree-node[data-node-id="${draggedId}"]`
    ) as HTMLElement | null
    const targetEl = document.querySelector(
      `#configuration-tree mat-tree-node[data-node-id="${targetId}"]`
    ) as HTMLElement | null

    if (!grabbedEl || !targetEl) {
      return
    }

    // Use layout positions unaffected by transforms/transitions.
    // `offsetTop` is stable even when the grabbed row is translated.
    let deltaY: number | null = null
    if (grabbedEl.offsetParent && grabbedEl.offsetParent === targetEl.offsetParent) {
      deltaY = targetEl.offsetTop - grabbedEl.offsetTop
    } else {
      const grabbedTop = grabbedEl.getBoundingClientRect().top
      const targetTop = targetEl.getBoundingClientRect().top
      deltaY = targetTop - grabbedTop
    }
    grabbedEl.style.transform = `translateY(${deltaY}px)`
  }

  private clearKeyboardDragPreviewTransform() {
    const els = document.querySelectorAll('#configuration-tree mat-tree-node.keyboard-drag-preview') as NodeListOf<HTMLElement>
    els.forEach(el => {
      el.style.transform = ''
    })
  }

  private scheduleKeyboardTemplatePreviewPositionUpdate() {
    setTimeout(() => this.updateKeyboardTemplatePreviewPosition(), 0)
    setTimeout(() => this.updateKeyboardTemplatePreviewPosition(), 50)
  }

  private updateKeyboardTemplatePreviewPosition() {
    if (!this.keyboardTemplateDragActive || !this.keyboardTemplateSourceNode) {
      this.clearKeyboardTemplatePreviewPosition()
      return
    }

    let anchorEl: HTMLElement | null = null

    if (this._keyboardTemplatePreviewMode === 'template') {
      anchorEl = document.querySelector(
        `#template-library-tree mat-tree-node[data-node-id="${this.keyboardTemplateSourceNode.id}"]`
      ) as HTMLElement | null
    } else {
      const targetId = this.keyboardDragTargetNodeId
      if (targetId) {
        anchorEl = document.querySelector(
          `#configuration-tree mat-tree-node[data-node-id="${targetId}"]`
        ) as HTMLElement | null
      }
    }

    if (!anchorEl) {
      this.clearKeyboardTemplatePreviewPosition()
      return
    }

    const rect = anchorEl.getBoundingClientRect()
    this.keyboardTemplatePreviewStyle = {
      display: 'flex',
      transform: `translate3d(${rect.left}px, ${rect.top}px, 0)`,
      width: `${rect.width}px`,
    }
  }

  private clearKeyboardTemplatePreviewPosition() {
    this.keyboardTemplatePreviewStyle = { display: 'none' }
  }

  private commitKeyboardDrop() {
    if (!this.keyboardDragActive || !this.keyboardDragNodeId || !this.keyboardDragTargetNodeId) {
      return
    }

    const sourceNode = this.getKeyboardDraggedFlatNode()
    if (!sourceNode) {
      this.endKeyboardDrag()
      return
    }

    const targetId = this.keyboardDragTargetNodeId
    if (sourceNode.id === targetId) {
      this.endKeyboardDrag()
      return
    }

    const nodeAtDestFlatNode = this.treeControl.dataNodes.find(n => n.id === targetId)
    if (!nodeAtDestFlatNode || nodeAtDestFlatNode.level !== sourceNode.level) {
      this.endKeyboardDrag()
      return
    }

    const nodeLevel = this.dataConfigurationHandlingService.getNodeLevel(sourceNode.id)
    const isSameParent = targetId.split('/', nodeLevel + 1).join('/') === sourceNode.id.split('/', nodeLevel + 1).join('/')
    if (!isSameParent) {
      this.endKeyboardDrag()
      return
    }

    const targetSiblings = this.treeBuildingService.findNodeSiblings(this.dataSource.data, targetId)
    const sourceSiblings = this.treeBuildingService.findNodeSiblings(this.dataSource.data, sourceNode.id)
    const siblings = targetSiblings || sourceSiblings
    if (!siblings) {
      this.endKeyboardDrag()
      return
    }

    const sourceSiblingIndex = siblings.findIndex(s => s.id === sourceNode.id)
    const targetSiblingIndex = siblings.findIndex(s => s.id === targetId)
    if (sourceSiblingIndex === -1 || targetSiblingIndex === -1) {
      this.endKeyboardDrag()
      return
    }

    // Keyboard semantics:
    // - When moving down and dropping on a row, insert AFTER that row.
    // - When moving up and dropping on a row, insert BEFORE that row.
    let insertIndex = targetSiblingIndex
    if (targetSiblingIndex > sourceSiblingIndex) {
      insertIndex = targetSiblingIndex + 1
    }
    // Adjust for removal of the source item from the same siblings list.
    if (insertIndex > sourceSiblingIndex) {
      insertIndex -= 1
    }
    if (insertIndex === sourceSiblingIndex) {
      this.endKeyboardDrag()
      return
    }

    // Keep focus on the dropped (source) row after the tree rebuilds.
    // Node ids are index-based and can change after reorder, so use stable keys.
    const focusPath = this.keyboardDragPath || this.buildKeyboardDragPath(sourceNode)
    this._pendingKeyboardDropFocusPath = { ...focusPath }

    // Clear preview transform before the tree reorders.
    this.clearKeyboardDragPreviewTransform()

    this.handleReorderNode(sourceNode as any, insertIndex)
    this.updateExpandedStates(sourceNode, EXPANDED_STATES_OPTIONS.DRAG_DROP, nodeAtDestFlatNode)
    if (this.selectedConfiguration) {
      this.handleDragDropSelectConfiguration(sourceNode.level, this.selectedConfiguration.index, this.dataSource.data, sourceSiblingIndex, insertIndex)
    }

    this.endKeyboardDrag()

    // Restore focus to the dropped (moved) row.
    // This must run even if a confirm dialog was shown and cancelled (no tree rebuild).
    this.schedulePendingKeyboardDropFocusRestore()
  }

  private focusTreeRowById(nodeId: string) {
    const selector = `#configuration-tree mat-tree-node[data-node-id="${nodeId}"]`
    const tryFocus = () => {
      const el = document.querySelector(selector) as HTMLElement | null
      el?.focus()
    }
    setTimeout(tryFocus, 0)
    setTimeout(tryFocus, 50)
    setTimeout(tryFocus, 150)
  }

  private getKeyboardDraggedFlatNode(): TreeFlatNode | null {
    if (!this.keyboardDragActive) {
      return null
    }

    if (this.keyboardDragPath && this.treeControl) {
      const matched = this.findFlatNodeByDragPath(this.keyboardDragPath)
      if (matched) {
        return matched
      }
    }

    const draggedId = this.keyboardDragNodeId
    if (draggedId && this.treeControl) {
      return (this.treeControl.dataNodes || []).find(n => n.id === draggedId) || null
    }

    return null
  }

  private buildKeyboardDragPath(node: TreeFlatNode) {
    const path: {
      level: number
      configKey?: string | null
      zoneKey?: string | null
      componentKey?: string | null
      conditionKey?: string | null
    } = { level: node.level }

    const configNode = node.level === NODE_LEVELS.CONFIG_LEVEL ? node : this.findAncestorAtLevel(node, NODE_LEVELS.CONFIG_LEVEL)
    path.configKey = (configNode?.value?.zoneLayoutId?.id || configNode?.value?.zoneLayoutLangKey || null) as any

    if (node.level >= NODE_LEVELS.ZONE_LEVEL) {
      const zoneNode = node.level === NODE_LEVELS.ZONE_LEVEL ? node : this.findAncestorAtLevel(node, NODE_LEVELS.ZONE_LEVEL)
      path.zoneKey = (zoneNode?.value?.tagLangKey || null) as any
    }

    if (node.level >= NODE_LEVELS.COMPONENT_LEVEL) {
      const compNode = node.level === NODE_LEVELS.COMPONENT_LEVEL ? node : this.findAncestorAtLevel(node, NODE_LEVELS.COMPONENT_LEVEL)
      path.componentKey = (compNode?.value?.nameKey || null) as any
    }

    if (node.level >= NODE_LEVELS.CONDITION_LEVEL) {
      const condNode = node.level === NODE_LEVELS.CONDITION_LEVEL ? node : this.findAncestorAtLevel(node, NODE_LEVELS.CONDITION_LEVEL)
      path.conditionKey = this.extractConditionLangKey(condNode?.value)
    }

    return path
  }

  private extractConditionLangKey(value: any): string | null {
    if (!value || typeof value !== 'object') {
      return null
    }
    const values = Object.values(value)
    const first = values && values.length ? values[0] : null
    return typeof first === 'string' ? first : null
  }

  private findAncestorAtLevel(node: TreeFlatNode, level: number): TreeFlatNode | null {
    let current: TreeFlatNode | null = node
    while (current && current.level > level) {
      current = this.getParentNode(current)
    }
    return current && current.level === level ? current : null
  }

  private findFlatNodeByDragPath(path: { level: number; configKey?: string | null; zoneKey?: string | null; componentKey?: string | null; conditionKey?: string | null }): TreeFlatNode | null {
    const nodes = (this.treeControl?.dataNodes || [])
    for (const candidate of nodes) {
      if (!candidate || candidate.level !== path.level) {
        continue
      }

      if (!this.candidateMatchesPath(candidate, path)) {
        continue
      }

      return candidate
    }
    return null
  }

  private candidateMatchesPath(candidate: TreeFlatNode, path: { level: number; configKey?: string | null; zoneKey?: string | null; componentKey?: string | null; conditionKey?: string | null }): boolean {
    // Match current level stable key.
    if (path.level === NODE_LEVELS.CONFIG_LEVEL) {
      const key = (candidate?.value?.zoneLayoutId?.id || candidate?.value?.zoneLayoutLangKey || null) as any
      return !!path.configKey && key === path.configKey
    }

    if (path.level === NODE_LEVELS.ZONE_LEVEL) {
      if (!path.zoneKey) {
        return false
      }
      const zoneKey = candidate?.value?.tagLangKey
      if (zoneKey !== path.zoneKey) {
        return false
      }
      const config = this.findAncestorAtLevel(candidate, NODE_LEVELS.CONFIG_LEVEL)
      const configKey = (config?.value?.zoneLayoutId?.id || config?.value?.zoneLayoutLangKey || null) as any
      return !path.configKey || configKey === path.configKey
    }

    if (path.level === NODE_LEVELS.COMPONENT_LEVEL) {
      if (!path.componentKey) {
        return false
      }
      const compKey = candidate?.value?.nameKey
      if (compKey !== path.componentKey) {
        return false
      }
      const zone = this.findAncestorAtLevel(candidate, NODE_LEVELS.ZONE_LEVEL)
      const zoneKey = zone?.value?.tagLangKey
      if (path.zoneKey && zoneKey !== path.zoneKey) {
        return false
      }
      const config = this.findAncestorAtLevel(candidate, NODE_LEVELS.CONFIG_LEVEL)
      const configKey = (config?.value?.zoneLayoutId?.id || config?.value?.zoneLayoutLangKey || null) as any
      return !path.configKey || configKey === path.configKey
    }

    if (path.level === NODE_LEVELS.CONDITION_LEVEL) {
      if (!path.conditionKey) {
        return false
      }
      const candidateCondKey = this.extractConditionLangKey(candidate?.value)
      if (candidateCondKey !== path.conditionKey) {
        return false
      }
      const comp = this.findAncestorAtLevel(candidate, NODE_LEVELS.COMPONENT_LEVEL)
      const compKey = comp?.value?.nameKey
      if (path.componentKey && compKey !== path.componentKey) {
        return false
      }
      const zone = this.findAncestorAtLevel(candidate, NODE_LEVELS.ZONE_LEVEL)
      const zoneKey = zone?.value?.tagLangKey
      if (path.zoneKey && zoneKey !== path.zoneKey) {
        return false
      }
      const config = this.findAncestorAtLevel(candidate, NODE_LEVELS.CONFIG_LEVEL)
      const configKey = (config?.value?.zoneLayoutId?.id || config?.value?.zoneLayoutLangKey || null) as any
      return !path.configKey || configKey === path.configKey
    }

    return false
  }

  private _restorePendingFocus() {
    if (!this._pendingFocusSelector) {
      return
    }

    const selector = this._pendingFocusSelector
    // Clear first so we don't accidentally refocus multiple times.
    this._pendingFocusSelector = null

    // Let Angular/Material finish rendering before refocusing.
    // (NGXS updates + MatTree rendering can land after the first tick.)
    const tryFocus = () => {
      const el = document.querySelector(selector) as HTMLElement | null
      el?.focus()
    }

    setTimeout(tryFocus, 0)
    setTimeout(tryFocus, 50)
    setTimeout(tryFocus, 150)
  }

  private _restoreLastMenuTriggerFocus() {
    const tryFocus = () => {
      MenuComponent.lastTriggerElement?.focus()
    }

    setTimeout(tryFocus, 0)
    setTimeout(tryFocus, 50)
    setTimeout(tryFocus, 150)
  }

  filterChangeExpandedStatesWithOption(node: TreeFlatNode, option: EXPANDED_STATES_OPTIONS, destinationNode?: TreeFlatNode): string[] {
    const nodeLevelIndex = option === EXPANDED_STATES_OPTIONS.COPY_PASTE ? node.level + 2 : node.level + 1
    const nodeIndexes: number[] = node.id.split('/').map(x => Number(x))
    const expandedStatesWithLevel = this.dataConfigurationHandlingService.filterExpandedStatesWithLevel(node.level, nodeIndexes, this.expandedStates)

    switch (option) {
      case EXPANDED_STATES_OPTIONS.CLONE_BELOW:
        return expandedStatesWithLevel.filter(expandedState => {
          const expandedStateIndexes = expandedState.split('/').map(x => Number(x))
          return nodeIndexes[nodeLevelIndex] < expandedStateIndexes[nodeLevelIndex]
        })

      case EXPANDED_STATES_OPTIONS.COPY_PASTE:
        return expandedStatesWithLevel.filter(expandedState => {
          const expandedStateIndexes = expandedState.split('/').map(x => Number(x))
          return nodeIndexes[nodeLevelIndex - 1] === expandedStateIndexes[nodeLevelIndex - 1]
        })
      case EXPANDED_STATES_OPTIONS.DELETE:
        return expandedStatesWithLevel.filter(expandedState => {
          const expandedStateIndexes = expandedState.split('/').map(x => Number(x))
          if (expandedState.includes(node.id) && nodeIndexes[nodeLevelIndex] === expandedStateIndexes[nodeLevelIndex]) {
            this.expansionModel.deselect(expandedState)
          }
          return nodeIndexes[nodeLevelIndex] < expandedStateIndexes[nodeLevelIndex]
        })

      case EXPANDED_STATES_OPTIONS.DRAG_DROP:
        const destinationNodeIndexes: number[] = destinationNode.id.split('/').map(x => Number(x))
        const getExpandedStateWithLimit = (value: number, lowerLimit: number, upperLimit: number) => {
          return value >= lowerLimit && value <= upperLimit
        }

        return expandedStatesWithLevel.filter(expandedState => {
          const expandedStateIndexes = expandedState.split('/').map(x => Number(x))
          return nodeIndexes[nodeLevelIndex] < destinationNodeIndexes[nodeLevelIndex]
            ? getExpandedStateWithLimit(expandedStateIndexes[nodeLevelIndex], nodeIndexes[nodeLevelIndex], destinationNodeIndexes[nodeLevelIndex])
            : getExpandedStateWithLimit(expandedStateIndexes[nodeLevelIndex], destinationNodeIndexes[nodeLevelIndex], nodeIndexes[nodeLevelIndex])
        })

      default:
        return []
    }
  }

  updateExpandedStatesWhenDropFromTemplateLibrary(node: TreeNode, insertAtLast: boolean = false) {
    const expandedStates = this.expansionModel.selected
    const parentsIndex = node.id.split('/').map(x => Number(x))
    const nodeLevel = parentsIndex.length - 1
    let newExpandedState

    expandedStates.forEach(expandedState => {
      const parentId = expandedState.split('/').map(x => Number(x))

      if (parentsIndex[nodeLevel] <= parentId[nodeLevel]) {
        newExpandedState = this.dataConfigurationHandlingService.updateChildNodeId(expandedState, insertAtLast ? parentId[nodeLevel] : parentId[nodeLevel] + 1, nodeLevel - 1)
        this.expansionModel.toggle(expandedState)
        this.expansionModel.toggle(newExpandedState)
      }
    })
    this.setExpandedStatesToStore()
  }

  updateExpandedStates(
    node: TreeFlatNode, option: EXPANDED_STATES_OPTIONS,
    destinationNode?: TreeFlatNode, numberNewConfig?: number) {

    // Don't update expanded states if node level is condition level
    if (node && node.level === NODE_LEVELS.CONDITION_LEVEL) {
      return
    }

    let expandedStates: string[],
      newExpandedState: string

    if (option === EXPANDED_STATES_OPTIONS.COPY_PASTE && node === null) {
      expandedStates = this.expansionModel.selected
      expandedStates.forEach(expandedState => {
        const parentId = expandedState.split('/').map(x => Number(x))
        newExpandedState = this.dataConfigurationHandlingService.updateChildNodeId(expandedState, parentId[NODE_LEVELS_INDEX.CONFIG_LEVEL] + 1, NODE_LEVELS.CONFIG_LEVEL)
        this.expansionModel.toggle(expandedState)
        this.expansionModel.toggle(newExpandedState)
      })
    } else {
      expandedStates = option !== EXPANDED_STATES_OPTIONS.IMPORT_LEGACY
        ? this.filterChangeExpandedStatesWithOption(node, option, destinationNode)
        : this.expandedStates
      expandedStates.forEach(expandedState => {
        newExpandedState = this.dataConfigurationHandlingService
          .getNewExpandedStateWithOption(expandedState, node, option, destinationNode, numberNewConfig)
        this.expansionModel.toggle(expandedState)
        this.expansionModel.toggle(newExpandedState)
      })
    }
    this.setExpandedStatesToStore()
  }


  updateAssetViewLocation(parentNode: TreeNode) {
    parentNode.children.forEach(childrenNode => {
      const updateNode: TreeFlatNode = this.treeControl.dataNodes.find(n => childrenNode.id === n.id)
      const newAssetViewLocation: number[] = parentNode.value.assetViewId
        ? this.dataConfigurationHandlingService.getNewAssetViewLocation(updateNode.value.assetViewLocation, parentNode.value.assetViewGrid)
        : []
      if (newAssetViewLocation) {
        const updateData = {
          name: updateNode.name,
          tagType: updateNode.value.tagType,
          tagNumber: updateNode.value.tagNumber,
          zoneInspectionTypes: updateNode.value.zoneInspectionTypes,
          assetViewLocation: newAssetViewLocation
        }
        this.editNodeInTree(updateNode, updateData, true, false)
      }
    })
  }

  private _handlePostTreeUpdate(node: TreeNode, shouldUpdateAssetViewLocation: boolean, shouldRemoveAssetViewLocation: boolean) {
    if (!shouldUpdateAssetViewLocation && !shouldRemoveAssetViewLocation) {
      return
    }

    if (shouldUpdateAssetViewLocation || shouldRemoveAssetViewLocation) {
      this.updateAssetViewLocation(node)
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe()
    this.store.dispatch(new SelectConfiguration(null))
    this.store.dispatch(new SetExpandedStates([]))
    // this.store.dispatch(new CopyConfiguration(null))
  }

  handleSelectNode(node, isSetDefault: boolean = false) {
    if (node.level === NODE_LEVELS.CONFIG_LEVEL) {
      const isNodeExpanded = this.expandedStates.findIndex(state => state === node.id) !== -1
      if (!isNodeExpanded && !isSetDefault) {
        this.expansionModel.toggle(node.id)
        this.setExpandedStatesToStore()
      }
      this.store.dispatch(new SelectConfiguration(node))
    }
  }

  isHoveringAssetZoneMapBox(node) {
    if (node.level === NODE_LEVELS.ZONE_LEVEL && this.hoveringAssetZoneBox) {
      const position = node.value.assetViewLocation
      if (!position) {
        return false
      }
      const positionX = position[GRID_VIEW_POSITION_INDEX.POSITION_X]
      const positionY = position[GRID_VIEW_POSITION_INDEX.POSITION_Y]

      const inspectionTypes = node.value.zoneInspectionTypes as string[]
      const isSelectedInspectionTypes = intersection(inspectionTypes, this.filterInspectionTypes)
      return positionX === this.hoveringAssetZoneBox.positionX && positionY === this.hoveringAssetZoneBox.positionY && isSelectedInspectionTypes.length
    }
    return false
  }

  checkUpdatedAssetViewBackground(node: TreeFlatNode, newAssetViewId: string): boolean {
    if (node.level !== NODE_LEVELS.CONFIG_LEVEL) {
      return false
    }

    const nodeIndexes = node.id.split('/').map(value => Number(value))
    return nodeIndexes[NODE_LEVELS_INDEX.CONFIG_LEVEL] === this.selectedConfiguration.index &&
      node.value.assetViewId !== newAssetViewId
  }

  isDisabledTooltip(nodeId: string): boolean {
    if (!nodeId) {
      return true
    }

    const selectors = [`name ${nodeId}`, `zone-tag ${nodeId}`, `zone-inspection-types ${nodeId}`]
    const nodeContentWidth = selectors
      .map(document.getElementById.bind(document))
      .filter(item => !!item)
      .map((item: HTMLElement) => item.offsetWidth)
      .reduce((acc, current) => acc += current, 0)
    const maxNodeContentWidth = document.getElementById(nodeId)
      ? document.getElementById(nodeId).offsetWidth
      : 0
    return nodeContentWidth < maxNodeContentWidth
  }

  openAddPieceDialog(node: TreeFlatNode) {
    this.dialog.open(AddPieceDialogComponent, {
      data: {
        name: node.name,
        hasNodeChildren: this.hasChild(null, node),
        isPremadeField: false,
        title: this.translateService.instant('CONFIG_PIECE_ADD_TITLE'),
      },
      panelClass: 'add-piece-dialog'
    }).afterClosed().pipe(filter(data => !!data))
      .subscribe(result => {
        const { name, isIncludedAllSublayers, isAdminViewOnly } = result
        this.handleAddPieceToTemplateLibrary(node, name, isIncludedAllSublayers, isAdminViewOnly)
      })
  }

  handleAddPieceToTemplateLibrary(node: TreeFlatNode, pieceName: string, isIncludedAllSublayers: boolean, isAdminViewOnly: boolean) {
    this.store.dispatch(new AddTemplateLibraryPiece(node, pieceName, isIncludedAllSublayers, isAdminViewOnly)).subscribe(
      value => { },
      error => {
        this.snackbarService.openNotificationSnackbar(
          this.translateService.instant('CONFIG_PIECE_ADD_FAIL_MSG', { name: pieceName }),
          NOTIFICATION_TYPES.FAILED
        )
      },
      () => {
        this.store.dispatch(new GetTemplateLibraryPieces())
        this.snackbarService.openNotificationSnackbar(
          this.translateService.instant('CONFIG_PIECE_ADD_SUCCESS_MSG', { name: pieceName }),
          NOTIFICATION_TYPES.SUCCESS, null, Constants.snackbar.duration
        )
      }
    )
  }

  renderId(parentIndex: number[], nodeLevel: NODE_LEVELS) {
    return parentIndex.reduce((acc, current, index) => index <= nodeLevel + 1 ? `${acc}${index === 0 ? '' : '/'}${current}` : acc, '')
  }

  handleReorderException(nodeId: string, dropIndex: number, isDropBelow: boolean = false) {
    const visibleNodes = this.visibleNodes()
    const nodeAtDest = visibleNodes[dropIndex]
    const nextNode = visibleNodes[dropIndex + 1]
    const parentIndexNodeAtDest = nodeAtDest.id.split('/').map(x => Number(x))
    const nodeLevel = this.dataConfigurationHandlingService.getNodeLevel(nodeId)
    const nextNodeLevel = nextNode ? this.dataConfigurationHandlingService.getNodeLevel(nextNode.id) : ''
    const isSameParent = nodeAtDest.id.split('/', nodeLevel + 1).join('/') === nodeId.split('/', nodeLevel + 1).join('/')
    let newIndex: number = -1
    if (!isDropBelow) {
      return newIndex
    }

    switch (nodeLevel) {
      case NODE_LEVELS.CONFIG_LEVEL:
        if (nextNodeLevel === NODE_LEVELS.CONFIG_LEVEL || !nextNode) {
          newIndex = parentIndexNodeAtDest[NODE_LEVELS_INDEX.CONFIG_LEVEL]
        }

        break
      case NODE_LEVELS.ZONE_LEVEL:
      case NODE_LEVELS.COMPONENT_LEVEL:
      case NODE_LEVELS.CONDITION_LEVEL:
        if ((nextNodeLevel <= nodeLevel || !nextNode) && isSameParent) {
          newIndex = parentIndexNodeAtDest[nodeLevel + 1]
        }
        break

      default:

        break
    }
    return newIndex
  }

  handleExceptionDropFromTemplateLibrary(dropIndex: number, dropLevel: NODE_LEVELS_INDEX, dropNodeLevel: NODE_LEVELS) {
    const visibleNodes = this.visibleNodes()
    const nodeAtDest = visibleNodes[dropIndex - 1]
    const nextNode = visibleNodes[dropIndex]
    const parentIndexNodeAtDest = nodeAtDest.id.split('/').map(x => Number(x))
    const nodeAtDestLevel = this.dataConfigurationHandlingService.getNodeLevel(nodeAtDest.id)
    const nextNodeLevel = nextNode ? this.dataConfigurationHandlingService.getNodeLevel(nextNode.id) : ''

    let newId
    let result

    switch (dropNodeLevel) {
      case NODE_LEVELS.CONFIG_LEVEL:
        break
      case NODE_LEVELS.ZONE_LEVEL:
        if ((nextNodeLevel === NODE_LEVELS.CONFIG_LEVEL || !nextNodeLevel) && nodeAtDestLevel >= dropNodeLevel) {
          newId = this.renderId(parentIndexNodeAtDest, dropNodeLevel)
          result = visibleNodes.find(x => x.id === newId)
          break
        }

        if ((nextNodeLevel === NODE_LEVELS.CONFIG_LEVEL || !nextNodeLevel) && !nodeAtDest.children.length) {
          result = {
            ...nodeAtDest,
            id: `${nodeAtDest.id}/0`
          }
          break
        }
        break
      case NODE_LEVELS.COMPONENT_LEVEL:
        if ((nextNodeLevel === NODE_LEVELS.CONFIG_LEVEL || nextNodeLevel === NODE_LEVELS.ZONE_LEVEL || !nextNodeLevel) && (nodeAtDestLevel > NODE_LEVELS.ZONE_LEVEL)) {
          newId = this.renderId(parentIndexNodeAtDest, dropNodeLevel)
          result = visibleNodes.find(x => x.id === newId)
          break
        }

        if ((nextNodeLevel === NODE_LEVELS.CONFIG_LEVEL || nextNodeLevel === NODE_LEVELS.ZONE_LEVEL || !nextNodeLevel) && !nodeAtDest.children.length && nodeAtDestLevel === NODE_LEVELS.ZONE_LEVEL) {
          result = {
            ...nodeAtDest,
            id: `${nodeAtDest.id}/0`
          }
          break
        }
        break
      case NODE_LEVELS.CONDITION_LEVEL:
        if (nodeAtDestLevel >= dropNodeLevel) {
          newId = this.renderId(parentIndexNodeAtDest, dropNodeLevel)
          result = visibleNodes.find(x => x.id === newId)
          break
        }

        if (nextNodeLevel <= NODE_LEVELS.COMPONENT_LEVEL && !nodeAtDest.children.length && nodeAtDestLevel === NODE_LEVELS.COMPONENT_LEVEL) {
          result = {
            ...nodeAtDest,
            id: `${nodeAtDest.id}/0`
          }
          break
        }
        break
      default:

        break
    }
    return result
  }
}
